import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketAPI, userAPI, fileAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Bug,
  Lightbulb,
  RefreshCw,
  Clock,
  User,
  Building,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  PlayCircle,
  MessageSquare,
  Calendar,
  Code,
  Paperclip,
  FileText,
  Image,
  File,
  Download,
  X,
  Boxes
} from 'lucide-react';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [comment, setComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [users, setUsers] = useState([]);
  const [resolutionTimeframe, setResolutionTimeframe] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [moveToDevDialogOpen, setMoveToDevDialogOpen] = useState(false);
  const [moveToDevData, setMoveToDevData] = useState({
    sprint_bucket: 'new',
    start_date: '',
    due_date: ''
  });

  useEffect(() => {
    fetchTicket();
    fetchAttachments();
    if (['admin', 'pm', 'support'].includes(user?.role)) {
      fetchUsers();
    }
  }, [id, user]);

  const fetchTicket = async () => {
    try {
      const response = await ticketAPI.getTicket(id);
      setTicket(response.data);
      setResolutionTimeframe(response.data.resolution_timeframe || '');
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await fileAPI.getTicketFiles(id);
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`${file.name} is too large. Max size is 10MB`);
        continue;
      }
      
      setUploadingFile(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fileAPI.upload(formData, id);
        setAttachments(prev => [...prev, response.data]);
      } catch (error) {
        console.error('Failed to upload file:', error);
        alert(`Failed to upload ${file.name}`);
      } finally {
        setUploadingFile(false);
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await fileAPI.delete(fileId);
      setAttachments(prev => prev.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.includes('pdf') || contentType?.includes('document')) return FileText;
    return File;
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await ticketAPI.updateTicket(id, { status: newStatus });
      setTicket(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Failed to update status:', error);
      alert(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleAssignChange = async (userId) => {
    setUpdating(true);
    try {
      await ticketAPI.updateTicket(id, { assigned_to: userId });
      await fetchTicket();
    } catch (error) {
      console.error('Failed to assign ticket:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleResolutionTimeframe = async () => {
    if (!resolutionTimeframe) return;
    setUpdating(true);
    try {
      await ticketAPI.updateTicket(id, { resolution_timeframe: resolutionTimeframe });
      setTicket(prev => ({ ...prev, resolution_timeframe: resolutionTimeframe }));
    } catch (error) {
      console.error('Failed to update timeframe:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    setAddingComment(true);
    try {
      await ticketAPI.addComment(id, comment);
      await fetchTicket();
      setComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setAddingComment(false);
    }
  };

  const handleMoveToDevelopment = async () => {
    setUpdating(true);
    try {
      const result = await ticketAPI.moveToDevelopment(id, moveToDevData);
      setMoveToDevDialogOpen(false);
      await fetchTicket();
      // Navigate to work items page to see the new task
      if (result.data.task) {
        alert(`Task ${result.data.task.task_number} created successfully!`);
      }
    } catch (error) {
      console.error('Failed to move to development:', error);
      alert(error.response?.data?.detail || 'Failed to move to development');
    } finally {
      setUpdating(false);
    }
  };

  const getTypeIcon = () => {
    switch (ticket?.ticket_type) {
      case 'bug': return <Bug className="h-5 w-5" />;
      case 'requirement': return <Lightbulb className="h-5 w-5" />;
      case 'change_request': return <RefreshCw className="h-5 w-5" />;
      default: return null;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="h-4 w-4" />;
      case 'wip': return <PlayCircle className="h-4 w-4" />;
      case 'resolved': return <CheckCircle className="h-4 w-4" />;
      case 'closed': return <XCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open': return 'status-open';
      case 'wip': return 'status-wip';
      case 'resolved': return 'status-resolved';
      case 'closed': return 'status-closed';
      default: return '';
    }
  };

  const canChangeStatus = () => {
    if (!ticket) return false;
    return (
      user?.role === 'admin' ||
      user?.role === 'pm' ||
      user?.role === 'support' ||
      ticket.assigned_to?.id === user?.id
    );
  };

  const canCloseTicket = () => {
    return ticket?.created_by?.id === user?.id;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div className="max-w-4xl mx-auto" data-testid="ticket-detail-page">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/tickets')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tickets
        </button>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-sm flex-shrink-0 ${
              ticket.ticket_type === 'bug' ? 'bg-red-100 text-red-600' :
              ticket.ticket_type === 'requirement' ? 'bg-blue-100 text-blue-600' :
              'bg-purple-100 text-purple-600'
            }`}>
              {getTypeIcon()}
            </div>
            <div className="min-w-0">
              <p className="font-mono text-sm text-primary font-semibold mb-1" data-testid="ticket-detail-number">
                {ticket.ticket_number}
              </p>
              <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl font-black tracking-tight">
                {ticket.title}
              </h1>
              <p className="text-muted-foreground mt-1 capitalize">
                {ticket.ticket_type.replace('_', ' ')} • {ticket.system}
              </p>
            </div>
          </div>
          <Badge className={`${getStatusClass(ticket.status)} text-sm sm:text-base px-3 sm:px-4 py-1.5 sm:py-2 flex-shrink-0 self-start`}>
            {getStatusIcon(ticket.status)}
            <span className="ml-2">{ticket.status.toUpperCase()}</span>
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="border rounded-sm p-6">
            <h3 className="font-semibold mb-4">Description</h3>
            <p className="text-muted-foreground whitespace-pre-wrap">
              {ticket.description}
            </p>
          </Card>

          {/* Comments */}
          <Card className="border rounded-sm overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Notes & Comments ({ticket.notes?.length || 0})
              </h3>
            </div>
            
            <div className="divide-y divide-border">
              {ticket.notes?.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  No comments yet
                </div>
              ) : (
                ticket.notes?.map((note) => (
                  <div key={note.id} className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                        {note.created_by.name[0]}
                      </div>
                      <span className="font-semibold text-sm">{note.created_by.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(note.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground pl-10">
                      {note.content}
                    </p>
                  </div>
                ))
              )}
            </div>

            {/* Add Comment */}
            <div className="p-4 border-t border-border">
              <Textarea
                placeholder="Add a comment..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="rounded-sm mb-3"
                rows={3}
                data-testid="comment-input"
              />
              <Button
                onClick={handleAddComment}
                disabled={addingComment || !comment.trim()}
                className="rounded-sm gap-2"
                data-testid="add-comment-btn"
              >
                {addingComment ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Add Comment
              </Button>
            </div>
          </Card>

          {/* Attachments */}
          <Card className="border rounded-sm overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Attachments ({attachments.length})
              </h3>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
                  data-testid="file-input-detail"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                  className="rounded-sm gap-2"
                  data-testid="upload-file-btn"
                >
                  {uploadingFile ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Paperclip className="h-4 w-4" />
                  )}
                  {uploadingFile ? 'Uploading...' : 'Add File'}
                </Button>
              </div>
            </div>
            
            <div className="divide-y divide-border">
              {attachments.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  No attachments yet
                </div>
              ) : (
                attachments.map((file) => {
                  const FileIcon = getFileIcon(file.content_type);
                  return (
                    <div key={file.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileIcon className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{file.original_filename}</p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB • {file.uploaded_by?.name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={fileAPI.getDownloadUrl(file.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-muted rounded-sm text-muted-foreground hover:text-foreground"
                          data-testid={`download-file-${file.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        {(file.uploaded_by?.id === user?.id || user?.role === 'admin') && (
                          <button
                            onClick={() => handleDeleteFile(file.id)}
                            className="p-2 hover:bg-destructive/10 rounded-sm text-muted-foreground hover:text-destructive"
                            data-testid={`delete-file-${file.id}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Details */}
          <Card className="border rounded-sm p-6 space-y-4">
            <h3 className="font-semibold border-b border-border pb-2">Details</h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Building className="h-4 w-4" /> Department
                </span>
                <span className="font-medium">{ticket.department}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <User className="h-4 w-4" /> Created by
                </span>
                <span className="font-medium">{ticket.created_by.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Created
                </span>
                <span className="font-medium">
                  {new Date(ticket.created_at).toLocaleDateString()}
                </span>
              </div>
              {ticket.resolution_timeframe && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4" /> ETA
                  </span>
                  <span className="font-medium">{ticket.resolution_timeframe}</span>
                </div>
              )}
              {ticket.in_development && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Code className="h-4 w-4" /> In Development
                  </span>
                  <Badge className="bg-green-100 text-green-700">Yes</Badge>
                </div>
              )}
            </div>
          </Card>

          {/* Assignment */}
          <Card className="border rounded-sm p-6 space-y-4">
            <h3 className="font-semibold border-b border-border pb-2">Assignment</h3>
            
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Assigned to</span>
              {canChangeStatus() ? (
                <Select
                  value={ticket.assigned_to?.id || ''}
                  onValueChange={handleAssignChange}
                >
                  <SelectTrigger className="rounded-sm" data-testid="assign-select">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="font-medium">{ticket.assigned_to?.name || 'Unassigned'}</p>
              )}
            </div>
          </Card>

          {/* Status Actions */}
          {(canChangeStatus() || canCloseTicket()) && (
            <Card className="border rounded-sm p-6 space-y-4">
              <h3 className="font-semibold border-b border-border pb-2">Actions</h3>
              
              {/* Resolution Timeframe */}
              {canChangeStatus() && (
                <div className="space-y-2">
                  <span className="text-sm text-muted-foreground">Resolution Timeframe</span>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., 2 days"
                      value={resolutionTimeframe}
                      onChange={(e) => setResolutionTimeframe(e.target.value)}
                      className="rounded-sm"
                      data-testid="timeframe-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResolutionTimeframe}
                      disabled={updating}
                      className="rounded-sm"
                      data-testid="set-timeframe-btn"
                    >
                      Set
                    </Button>
                  </div>
                </div>
              )}

              {/* Status Buttons */}
              <div className="space-y-2">
                <span className="text-sm text-muted-foreground">Change Status</span>
                <div className="grid grid-cols-2 gap-2">
                  {canChangeStatus() && ticket.status === 'open' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange('wip')}
                      disabled={updating}
                      className="rounded-sm status-wip text-black"
                      data-testid="status-wip-btn"
                    >
                      Start Work
                    </Button>
                  )}
                  {canChangeStatus() && ticket.status === 'wip' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange('resolved')}
                      disabled={updating}
                      className="rounded-sm status-resolved"
                      data-testid="status-resolved-btn"
                    >
                      Resolve
                    </Button>
                  )}
                  {canCloseTicket() && ticket.status === 'resolved' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange('closed')}
                      disabled={updating}
                      className="rounded-sm status-closed"
                      data-testid="status-closed-btn"
                    >
                      Close Ticket
                    </Button>
                  )}
                </div>
              </div>

              {/* Move to Development (PM only for requirements/change requests) */}
              {['pm', 'admin'].includes(user?.role) && 
               ticket.ticket_type !== 'bug' && 
               !ticket.in_development && (
                <Dialog open={moveToDevDialogOpen} onOpenChange={setMoveToDevDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="w-full rounded-sm gap-2"
                      data-testid="move-dev-btn"
                    >
                      <Code className="h-4 w-4" />
                      Move to Development
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-sm">
                    <DialogHeader>
                      <DialogTitle className="font-heading flex items-center gap-2">
                        <Boxes className="h-5 w-5" />
                        Move to Development
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <p className="text-sm text-muted-foreground">
                        This will create a development task linked to ticket <strong>{ticket.ticket_number}</strong>
                      </p>
                      
                      <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                          Sprint Bucket *
                        </Label>
                        <Select 
                          value={moveToDevData.sprint_bucket} 
                          onValueChange={(v) => setMoveToDevData(prev => ({ ...prev, sprint_bucket: v }))}
                        >
                          <SelectTrigger className="rounded-sm" data-testid="bucket-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="current">Current Sprint</SelectItem>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="next">Next Sprint</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                            Start Date
                          </Label>
                          <Input
                            type="date"
                            value={moveToDevData.start_date}
                            onChange={(e) => setMoveToDevData(prev => ({ ...prev, start_date: e.target.value }))}
                            className="rounded-sm"
                            data-testid="start-date-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                            Due Date
                          </Label>
                          <Input
                            type="date"
                            value={moveToDevData.due_date}
                            onChange={(e) => setMoveToDevData(prev => ({ ...prev, due_date: e.target.value }))}
                            className="rounded-sm"
                            data-testid="due-date-input"
                          />
                        </div>
                      </div>

                      <Button 
                        onClick={handleMoveToDevelopment}
                        disabled={updating}
                        className="w-full rounded-sm gap-2"
                        data-testid="confirm-move-dev-btn"
                      >
                        {updating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Code className="h-4 w-4" />
                        )}
                        Create Development Task
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {/* Show linked task if exists */}
              {ticket.task_id && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                  <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                    <Code className="h-4 w-4" />
                    Linked Task: {ticket.task_number || 'TASK'}
                  </p>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
